import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { isSameVehicle } from '../utils/Utils_testable'
import { CART_VERSION_STRING } from 'common'
import { ProductLocationModel } from 'interfaces'
import { type LaborItem } from 'src/store/models/LaborModel'
import { type VehicleSpecificationCartItem } from 'src/store/models/VehicleSpecification'
import {
  ISeller,
  SellerCartFields,
} from '../../../../src/services/MultiSellerServicesProvider'
import { type Vehicle } from '../../../../src/store/models/Vehicles'
import {
  cleanupCart,
  clearSpecsAndLaborAfterOrder,
  createCart,
  findCartVehicle,
  getCartStorageKey,
  handleSellerPartQtyPriceChanges,
  isValidSellerFormField,
  removeProductFromCartVehicle,
  setQtyAtLocation,
  updateBundleInfo,
} from './helpers'
import {
  CartProduct,
  IBundleProduct,
  IRemovePartFromCartProps,
  // IAddPartToCartProps,
  // ISetQtyProps,
  ISetQtyPropsNew,
  SellerCartData,
  SellerOrderCartData,
  SellerWithLocations,
} from './ISellerCart'
import { DeliveryDetail, LocationTransports } from 'src/api/cart/interfaces'
import { Address } from 'src/api/user/interfaces'
import { OrderFormData } from 'src/store/models/ShoppingCartModels'
import { SellerPartInfoResponse } from 'src/store/cart/ValidationInterfaces'
import { trackRemoveFromCart } from '../utils/Utils'
import { GaTrackOption } from 'src/config/analytics/tracking/utils'

const getCartFromStorage = (userId: string): SellerCartData | null => {
  try {
    const raw = localStorage.getItem(getCartStorageKey(userId))
    if (!raw) return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

const saveCartToStorage = (userId: string, data: SellerCartData) => {
  try {
    localStorage.setItem(getCartStorageKey(userId), JSON.stringify(data))
  } catch {
    // eslint-disable-next-line no-console
    console.log(`error`)
  }
}

export interface ICartSlice {
  cartData: SellerCartData
  userId: string
  availableTransports: LocationTransports[]
  addresses: Address[]
  isInitialized: boolean
  isMultiCartValidated: boolean
  commonOrderFormData: CommonOrderFormData
  displayCartFormErrors: boolean
}

export interface CommonOrderFormData {
  poNumber: string
  noteToStore: string
}

export const defaultCart: SellerCartData = {
  version: CART_VERSION_STRING,
  vehicles: [],
}

export const defaultCommonOrderFormData: CommonOrderFormData = {
  poNumber: '',
  noteToStore: '',
}

export const initialState: ICartSlice = {
  cartData: defaultCart,
  userId: '',
  availableTransports: [],
  addresses: [],
  isInitialized: false,
  isMultiCartValidated: false,
  commonOrderFormData: defaultCommonOrderFormData,
  displayCartFormErrors: false,
}

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    restoreCart: (state, action: PayloadAction<string>) => {
      state.userId = action.payload
      const cartData = getCartFromStorage(action.payload)
      if (cartData) state.cartData = cartData
      state.isInitialized = true
    },
    setCartData: (state, action: PayloadAction<SellerCartData>) => {
      state.cartData = action.payload
    },
    setSellerData: (state, action: PayloadAction<SellerCartData>) => {
      state.cartData = action.payload
      saveCartToStorage(state.userId, state.cartData)
    },

    createCart: (
      state,
      action: PayloadAction<{
        vehicle: Vehicle
        seller?: ISeller
        location?: ProductLocationModel
      }>
    ) => {
      // 1. update cart using helper
      createCart(state, action.payload)

      // 2. auto-sync to sellerCartData + localStorage
      cartSlice.caseReducers.setSellerData(state, {
        type: 'cart/setSellerData',
        payload: state.cartData,
      })
    },

    sortVehicleToFront: (state, action: PayloadAction<Vehicle>) => {
      const vehicles = state.cartData.vehicles
      const index = vehicles.findIndex((v) =>
        isSameVehicle(v.vehicle, action.payload)
      )

      if (index > 0) {
        vehicles.unshift(...vehicles.splice(index, 1))
      }

      cartSlice.caseReducers.setSellerData(state, {
        type: 'cart/setSellerData',
        payload: state.cartData,
      })
    },
    // addPartToCart: (state, action: PayloadAction<IAddPartToCartProps>) => {
    //   //
    // },
    // updatePartInCart: (state, action: PayloadAction<ISetQtyProps>) => {
    //   //
    // },
    removeVehicleFromCart: (
      state,
      action: PayloadAction<{ vehicle: Vehicle; track: GaTrackOption }>
    ) => {
      const { vehicle, track } = action.payload
      // Track removal of all parts before removing the vehicle
      const cartVehicle = state.cartData.vehicles.find((v) =>
        isSameVehicle(v.vehicle, vehicle)
      )

      if (cartVehicle && track === GaTrackOption.track) {
        cartVehicle.sellers.forEach((seller) => {
          seller.locations.forEach((location) => {
            location.parts.forEach((part) => {
              trackRemoveFromCart(part, vehicle, part.quantityRequested)
            })
          })
        })
      }

      state.cartData.vehicles = state.cartData.vehicles.filter(
        (v) => !isSameVehicle(v.vehicle, vehicle)
      )

      // Persist to localStorage
      cartSlice.caseReducers.setSellerData(state, {
        type: 'cart/setSellerData',
        payload: state.cartData,
      })
    },

    setVehicleSpecifications: (
      state,
      action: PayloadAction<{
        vehicle: Vehicle
        specifications: Array<VehicleSpecificationCartItem>
      }>
    ) => {
      const { vehicle, specifications } = action.payload

      let cartVehicle = findCartVehicle(state, vehicle)

      if (!cartVehicle) {
        cartSlice.caseReducers.createCart(state, {
          type: 'cart/createCart',
          payload: { vehicle },
        })
        cartVehicle = findCartVehicle(state, vehicle)
      }

      if (!cartVehicle) {
        return
      }

      cartVehicle.specifications = [...specifications]

      // Persist to localStorage
      cartSlice.caseReducers.setSellerData(state, {
        type: 'cart/setSellerData',
        payload: state.cartData,
      })
    },

    setVehicleLaborData: (
      state,
      action: PayloadAction<{
        vehicle: Vehicle
        laborItems: Array<LaborItem>
      }>
    ) => {
      const { vehicle, laborItems } = action.payload

      let cartVehicle = findCartVehicle(state, vehicle)

      if (!cartVehicle) {
        cartSlice.caseReducers.createCart(state, {
          type: 'cart/createCart',
          payload: { vehicle },
        })
        cartVehicle = findCartVehicle(state, vehicle)
      }

      if (!cartVehicle) {
        return
      }

      cartVehicle.laborResults = [...laborItems]

      // Persist to localStorage
      cartSlice.caseReducers.setSellerData(state, {
        type: 'cart/setSellerData',
        payload: state.cartData,
      })
    },

    removeAllVehiclesFromCart: (
      state,
      action: PayloadAction<GaTrackOption>
    ) => {
      if (action.payload === GaTrackOption.track) {
        // Track removal of all parts from all vehicles
        state.cartData.vehicles.forEach((cartVehicle) => {
          cartVehicle.sellers.forEach((seller) => {
            seller.locations.forEach((location) => {
              location.parts.forEach((part) => {
                trackRemoveFromCart(
                  part,
                  cartVehicle.vehicle,
                  part.quantityRequested
                )
              })
            })
          })
        })
      }

      state.cartData = defaultCart

      // Persist to localStorage
      cartSlice.caseReducers.setSellerData(state, {
        type: 'cart/setSellerData',
        payload: state.cartData,
      })
    },

    removePartFromCart: (
      state,
      action: PayloadAction<IRemovePartFromCartProps>
    ) => {
      removeProductFromCartVehicle(state, action.payload)
      // Persist to localStorage
      cartSlice.caseReducers.setSellerData(state, {
        type: 'cart/setSellerData',
        payload: state.cartData,
      })
    },
    cleanupCart: (state, action: PayloadAction<Vehicle>) => {
      cleanupCart(state, action.payload)
      // Persist to localStorage
      cartSlice.caseReducers.setSellerData(state, {
        type: 'cart/setSellerData',
        payload: state.cartData,
      })
    },

    clearSpecsAndLaborAfterOrder: (state) => {
      clearSpecsAndLaborAfterOrder(state)
      // Persist to localStorage
      cartSlice.caseReducers.setSellerData(state, {
        type: 'cart/setSellerData',
        payload: state.cartData,
      })
    },

    setQtyAtLocation: (state, action: PayloadAction<ISetQtyPropsNew>) => {
      setQtyAtLocation(state, action.payload)
      // Persist to localStorage
      cartSlice.caseReducers.setSellerData(state, {
        type: 'cart/setSellerData',
        payload: state.cartData,
      })
    },
    updateBundleInfo: (
      state,
      action: PayloadAction<SellerOrderCartData<IBundleProduct>[]>
    ) => {
      updateBundleInfo(state, action.payload)
      // 2. auto-trigger setSellerData (MobX behavior)
      cartSlice.caseReducers.setSellerData(state, {
        type: 'cart/setSellerData',
        payload: state.cartData,
      })
    },

    setFieldOnSeller: (
      state,
      action: PayloadAction<{
        field: SellerCartFields
        vehicle: Vehicle
        seller: SellerWithLocations
        value: string
      }>
    ) => {
      const { field, vehicle, seller, value } = action.payload

      const sellerData = state.cartData
      if (!sellerData) return

      const sellerCart = findCartVehicle(state, vehicle)?.sellers.find(
        (s) => s.coverageKey === seller.coverageKey
      )

      if (!sellerCart) return

      const fieldsToUpdate = { [field]: value } as Record<
        SellerCartFields,
        string
      >

      Object.entries(fieldsToUpdate).forEach(([field, value]) => {
        if (isValidSellerFormField(field)) {
          sellerCart[field as SellerCartFields] = value
        }
      })
      // Persist to localStorage
      cartSlice.caseReducers.setSellerData(state, {
        type: 'cart/setSellerData',
        payload: state.cartData,
      })
    },

    setFieldOnVehicleCart: (
      state,
      action: PayloadAction<{ field: string; vehicle: Vehicle; value: string }>
    ) => {
      const { field, vehicle, value } = action.payload

      const sellerData = state.cartData
      for (const sellerCartVehicle of sellerData.vehicles) {
        if (isSameVehicle(vehicle, sellerCartVehicle.vehicle)) {
          sellerCartVehicle.orderFormData = {
            ...sellerCartVehicle.orderFormData,
            [field]: value,
          }
        }
      }
      // Persist to localStorage
      cartSlice.caseReducers.setSellerData(state, {
        type: 'cart/setSellerData',
        payload: state.cartData,
      })
    },

    setAvailableTransports: (
      state,
      action: PayloadAction<LocationTransports[]>
    ) => {
      state.availableTransports = action.payload
    },

    setAddresses: (state, action: PayloadAction<Address[]>) => {
      state.addresses = action.payload
    },

    setVehicleOrderFormData: (
      state,
      action: PayloadAction<{
        vehicle: Vehicle
        quoteSourceId: string
        orderformData: OrderFormData
        sellerQuoteIds?: number[]
      }>
    ) => {
      const { vehicle, quoteSourceId, orderformData, sellerQuoteIds } =
        action.payload
      const cartVehicle = findCartVehicle(state, vehicle)
      if (!cartVehicle) return
      cartVehicle.quoteSourceId = quoteSourceId
      cartVehicle.orderFormData = orderformData
      cartVehicle.sellerQuoteIds = sellerQuoteIds

      cartVehicle.sellers = cartVehicle.sellers.map((s) => {
        return {
          ...s,
          poNumber: cartVehicle.orderFormData.poNumber,
          noteToStore: cartVehicle.orderFormData.noteToStore,
        }
      })
    },

    setCartInitialized: (state) => {
      state.isInitialized = true
    },

    setMultiCartValidated: (
      state,
      action: PayloadAction<{ value: boolean }>
    ) => {
      state.isMultiCartValidated = action.payload.value
    },
    handleSellerPartQtyPriceChanges: (
      state,
      action: PayloadAction<{
        cartProduct: CartProduct
        sellerPartInfo: SellerPartInfoResponse
      }>
    ) => {
      handleSellerPartQtyPriceChanges(
        state,
        action.payload.cartProduct,
        action.payload.sellerPartInfo
      )
    },

    setCommonOrderFormData: (
      state,
      action: PayloadAction<CommonOrderFormData>
    ) => {
      state.commonOrderFormData = {
        ...action.payload,
      }
    },
    setDisplayCartFormErrors: (state, action: PayloadAction<boolean>) => {
      state.displayCartFormErrors = action.payload
    },
    emptyCommonOrderFormData: (state) => {
      state.commonOrderFormData = {
        poNumber: '',
        noteToStore: '',
      }
    },

    updateVehicleSubModel: (
      state,
      action: PayloadAction<{
        vehicle: Vehicle
        subModel: Vehicle['subModel']
      }>
    ) => {
      const { vehicle, subModel } = action.payload
      const cartVehicle = findCartVehicle(state, vehicle)
      if (!cartVehicle) return

      cartVehicle.vehicle = { ...cartVehicle.vehicle, subModel }

      cartSlice.caseReducers.setSellerData(state, {
        type: 'cart/setSellerData',
        payload: state.cartData,
      })
    },

    updateLocationDeliveryDetails: (
      state,
      action: PayloadAction<{
        vehicle: Vehicle
        locationId: string
        deliveryDetail: DeliveryDetail
      }>
    ) => {
      const { vehicle, locationId, deliveryDetail } = action.payload
      const cartVehicle = findCartVehicle(state, vehicle)
      if (!cartVehicle) return

      cartVehicle.sellers.forEach((seller) => {
        seller.locations.forEach((location) => {
          if (location.locationId.toString() === locationId.toString()) {
            location.deliveryDetail = deliveryDetail
          }
        })
      })

      cartSlice.caseReducers.setSellerData(state, {
        type: 'cart/setSellerData',
        payload: state.cartData,
      })
    },
  },
})

// Export actions
export const {
  setSellerData,
  setAvailableTransports,
  setAddresses,
  setCartInitialized,
  setMultiCartValidated,
  updateLocationDeliveryDetails,
  sortVehicleToFront,
} = cartSlice.actions

export { cartSlice }
export const cartReducer = cartSlice.reducer
